import {
  MAX_MESSAGE_FILES,
  MAX_MESSAGE_LENGTH,
  type ConversationListItem,
  type MessageItem,
} from '@hemmafixa/shared';
import {
  Avatar,
  Button,
  Card,
  ContextPanel,
  EmptyState,
  FileGallery,
  PageHeader,
  Spinner,
  Textarea,
  cn,
  toast,
  useMasterDetailWide,
} from '@hemmafixa/ui';
import { ArrowLeft, FileText, Lock, MessageSquare, Paperclip, Send, X } from 'lucide-react';
import { Fragment, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import { ContactDetailsList } from '../shared/contact-details';
import { ConversationInfo } from './conversation-info';
import { galleryItem, isImage, putToStorage, useFileTexts } from '../shared/file-uploads';
import { QueryView } from '../shared/query-view';
import { ReportButton, ReportPanel } from '../shared/report-button';
import {
  messageFileSteps,
  useConversation,
  useConversations,
  useMarkRead,
  useMessages,
  useSendMessage,
} from './api';

/** Conversations for clients and providers (P5-03): the list beside the open chat, one at a time on phones. */
export function MessagesPage() {
  const { t } = useI18n();
  const query = useConversations();
  const [params, setParams] = useSearchParams();
  const wide = useMasterDetailWide();
  // From `lg` a chat is always open: the newest one until another is picked
  const selectedId = params.get('c') ?? (wide ? (query.data?.items[0]?.id ?? null) : null);
  const select = (id: string | null) => setParams(id ? { c: id } : {});

  return (
    <div className="grid gap-4">
      <PageHeader title={t.messages.title} description={t.messages.description} />
      <QueryView query={query}>
        {({ items }) =>
          items.length === 0 ? (
            <EmptyState icon={MessageSquare} title={t.messages.empty} description={t.messages.emptyText} />
          ) : (
            <Card className="grid h-[calc(100dvh-11rem)] min-h-[28rem] overflow-hidden lg:grid-cols-[18rem_minmax(0,1fr)]">
              <ConversationList
                items={items}
                selectedId={selectedId}
                onSelect={select}
                className={cn(selectedId && 'hidden lg:block')}
              />
              {selectedId ? (
                <Thread key={selectedId} id={selectedId} onBack={() => select(null)} />
              ) : (
                <div className="hidden items-center justify-center p-6 text-sm text-muted-foreground lg:flex">
                  {t.messages.pick}
                </div>
              )}
            </Card>
          )
        }
      </QueryView>
    </div>
  );
}

interface ConversationListProps {
  items: ConversationListItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  className?: string;
}

function ConversationList({ items, selectedId, onSelect, className }: ConversationListProps) {
  const { t, relative } = useI18n();
  const preview = (item: ConversationListItem) => {
    const last = item.lastMessage;
    if (!last) return t.messages.noMessages;
    const text = last.body || (last.fileCount > 0 ? fill(t.messages.files, { count: last.fileCount }) : '');
    return last.fromMe ? fill(t.messages.you, { text }) : text;
  };

  return (
    <ul className={cn('divide-y divide-border overflow-y-auto border-border lg:border-r', className)}>
      {items.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            onClick={() => onSelect(item.id)}
            aria-current={item.id === selectedId ? 'true' : undefined}
            className={cn(
              'flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/60',
              item.id === selectedId && 'bg-muted',
            )}
          >
            <Avatar name={item.otherName} kind={item.side === 'client' ? 'company' : 'person'} size="md" />
            <span className="grid min-w-0 flex-1 gap-0.5">
              <span className="flex items-center justify-between gap-2">
                <span className={cn('truncate', item.unreadCount > 0 ? 'font-semibold' : 'font-medium')}>
                  {item.otherName}
                </span>
                {item.unreadCount > 0 ? (
                  <span
                    className="rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground"
                    aria-label={fill(t.messages.unread, { count: item.unreadCount })}
                  >
                    {item.unreadCount}
                  </span>
                ) : (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {relative(item.lastMessageAt)}
                  </span>
                )}
              </span>
              <span className="truncate text-xs text-muted-foreground">{item.job.title}</span>
              <span className="truncate text-sm text-muted-foreground">{preview(item)}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function Thread({ id, onBack }: { id: string; onBack: () => void }) {
  const { t } = useI18n();
  const conversation = useConversation(id);
  const messages = useMessages(id);
  const markRead = useMarkRead();
  const [showContact, setShowContact] = useState(false);
  const newest = messages.data?.pages[0]?.[0];

  // Opening the chat, and every message that arrives while it is open, counts as read
  const { mutate: read } = markRead;
  useEffect(() => {
    if (newest && !newest.fromMe) read(id);
  }, [id, newest, read]);

  const detail = conversation.data;
  return (
    <section aria-label={detail?.otherName ?? t.messages.title} className="flex min-h-0 flex-col">
      <header className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Button variant="ghost" size="icon" className="lg:hidden" aria-label={t.common.back} onClick={onBack}>
          <ArrowLeft />
        </Button>
        {detail && (
          <Avatar name={detail.otherName} kind={detail.side === 'client' ? 'company' : 'person'} size="md" />
        )}
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-semibold">{detail?.otherName}</h2>
          {detail && (
            <Link
              to={`/${detail.side}/jobs/${detail.job.id}`}
              className="block truncate text-xs text-muted-foreground underline-offset-4 hover:underline"
            >
              {detail.job.title}
            </Link>
          )}
        </div>
        {detail && (
          <Button
            variant="outline"
            size="sm"
            className="xl:hidden"
            aria-expanded={showContact}
            onClick={() => setShowContact((v) => !v)}
          >
            {t.job.contactTitle}
          </Button>
        )}
      </header>
      {detail && showContact && (
        <div className="border-b border-border p-4 xl:hidden">
          <ContactDetailsList contact={detail.contact} />
        </div>
      )}
      {detail && (
        <ContextPanel className="hidden xl:grid">
          <ConversationInfo detail={detail} />
        </ContextPanel>
      )}
      <QueryView query={messages}>
        {(data) => (
          <MessageList
            messages={data.pages.flat()}
            hasOlder={messages.hasNextPage}
            loadingOlder={messages.isFetchingNextPage}
            onLoadOlder={() => void messages.fetchNextPage()}
          />
        )}
      </QueryView>
      {detail &&
        (detail.status === 'open' ? (
          <Composer conversationId={id} />
        ) : (
          <p className="flex items-center gap-2 border-t border-border p-4 text-sm text-muted-foreground">
            <Lock className="size-4 shrink-0" aria-hidden />
            {detail.status === 'blocked' ? t.messages.blocked : t.messages.closed}
          </p>
        ))}
    </section>
  );
}

interface MessageListProps {
  /** Newest first, as the API sends them */
  messages: MessageItem[];
  hasOlder: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
}

function MessageList({ messages, hasOlder, loadingOlder, onLoadOlder }: MessageListProps) {
  const { t, dateTime } = useI18n();
  const files = useFileTexts();
  const end = useRef<HTMLLIElement>(null);
  const newestId = messages[0]?.id;
  /** The message being reported; its form opens under it */
  const [reporting, setReporting] = useState<string | null>(null);

  // Follow the newest message, not older pages loaded above
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [newestId]);

  return (
    <ol className="flex flex-1 flex-col gap-3 overflow-y-auto p-4" aria-live="polite">
      {hasOlder && (
        <li className="self-center">
          <Button variant="ghost" size="sm" loading={loadingOlder} onClick={onLoadOlder}>
            {t.messages.older}
          </Button>
        </li>
      )}
      {messages.length === 0 && <li className="m-auto text-sm text-muted-foreground">{t.messages.start}</li>}
      {[...messages].reverse().map((message) => (
        <Fragment key={message.id}>
          {message.kind === 'system' ? (
            <li key={message.id} className="self-center text-center text-xs text-muted-foreground">
              {message.body}
            </li>
          ) : (
            <li
              key={message.id}
              className={cn(
                'grid max-w-[85%] gap-2 rounded-xl px-4 py-2 text-sm',
                message.fromMe
                  ? 'self-end rounded-br-sm bg-secondary text-secondary-foreground'
                  : 'self-start rounded-bl-sm bg-muted',
              )}
            >
              {message.hidden ? (
                <p className="italic">{t.messages.hidden}</p>
              ) : (
                message.body && <p className="whitespace-pre-line break-words">{message.body}</p>
              )}
              {message.attachments.length > 0 && (
                <FileGallery items={message.attachments.map(galleryItem)} labels={files.gallery} />
              )}
              <span className="flex items-center justify-between gap-2">
                <time
                  dateTime={message.createdAt}
                  className={cn(
                    'text-[0.6875rem]',
                    message.fromMe ? 'text-secondary-foreground/70' : 'text-muted-foreground',
                  )}
                >
                  {dateTime(message.createdAt)}
                </time>
                {message.fromMe === false && !message.hidden && (
                  <ReportButton compact className="-my-1 -mr-2" onClick={() => setReporting(message.id)} />
                )}
              </span>
            </li>
          )}
          {reporting === message.id && (
            <li className="self-stretch">
              <ReportPanel target="message" targetId={message.id} onClose={() => setReporting(null)} />
            </li>
          )}
        </Fragment>
      ))}
      <li ref={end} aria-hidden />
    </ol>
  );
}

interface PendingFile {
  key: string;
  name: string;
  image: boolean;
  /** Set once the file is uploaded and checked */
  uploadId?: string;
  failed?: boolean;
}

/** Text and files; Enter sends, Shift+Enter adds a line. Files upload as soon as they are chosen. */
function Composer({ conversationId }: { conversationId: string }) {
  const { t } = useI18n();
  const send = useSendMessage(conversationId);
  const [body, setBody] = useState('');
  const [pending, setPending] = useState<PendingFile[]>([]);
  const input = useRef<HTMLInputElement>(null);
  const uploading = pending.some((file) => !file.uploadId && !file.failed);
  const ready = pending.flatMap((file) => (file.uploadId ? [file.uploadId] : []));
  const canSend = !uploading && !send.isPending && (body.trim().length > 0 || ready.length > 0);

  const addFiles = async (list: FileList | null) => {
    const chosen = [...(list ?? [])].slice(0, MAX_MESSAGE_FILES - pending.length);
    if (input.current) input.current.value = '';
    for (const file of chosen) {
      const key = `${Date.now()}-${file.name}`;
      setPending((current) => [...current, { key, name: file.name, image: isImage(file.type) }]);
      try {
        const created = await messageFileSteps.create(file);
        await putToStorage(created, file);
        await messageFileSteps.complete(created.upload.id);
        setPending((current) =>
          current.map((p) => (p.key === key ? { ...p, uploadId: created.upload.id } : p)),
        );
      } catch {
        setPending((current) => current.map((p) => (p.key === key ? { ...p, failed: true } : p)));
      }
    }
  };

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (!canSend) return;
    send.mutate(
      { body: body.trim(), uploadIds: ready },
      {
        onSuccess: () => {
          setBody('');
          setPending([]);
        },
        onError: () => toast.error(t.messages.sendFailed),
      },
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    submit();
  };

  return (
    <form onSubmit={submit} className="grid gap-2 border-t border-border p-3">
      {pending.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {pending.map((file) => (
            <li
              key={file.key}
              className={cn(
                'inline-flex max-w-full items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs',
                file.failed && 'border-danger text-danger',
              )}
            >
              {!file.uploadId && !file.failed ? (
                <Spinner className="size-3.5" />
              ) : (
                <FileText className="size-3.5" aria-hidden />
              )}
              <span className="truncate">
                {file.failed ? fill(t.messages.fileFailed, { name: file.name }) : file.name}
              </span>
              <button
                type="button"
                aria-label={fill(t.files.remove, { name: file.name })}
                onClick={() => setPending((current) => current.filter((p) => p.key !== file.key))}
                className="rounded-full p-0.5 hover:bg-muted"
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-2">
        <input
          ref={input}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="sr-only"
          tabIndex={-1}
          aria-hidden
          onChange={(event) => void addFiles(event.target.files)}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t.messages.attach}
          disabled={pending.length >= MAX_MESSAGE_FILES}
          onClick={() => input.current?.click()}
        >
          <Paperclip />
        </Button>
        <Textarea
          rows={1}
          value={body}
          maxLength={MAX_MESSAGE_LENGTH}
          onChange={(event) => setBody(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t.messages.placeholder}
          aria-label={t.messages.placeholder}
          className="max-h-40 min-h-10 resize-none"
        />
        <Button
          type="submit"
          size="icon"
          aria-label={t.messages.send}
          disabled={!canSend}
          loading={send.isPending}
        >
          <Send />
        </Button>
      </div>
    </form>
  );
}
